import { authInitialState } from '@features/auth/model/reducer.js';
import {
  UserSchema,
  UserSettingsCompatSchema,
} from '@features/auth/model/types.js';
import { cachedMapsSettingsInitialState } from '@features/cachedMaps/model/settingsReducer.js';
import { cookieConsentInitialState } from '@features/cookieConsent/model/reducer.js';
import { dataViewerSettingsInitialState } from '@features/dataViewer/model/settingsReducer.js';
import {
  DrawingSettingsCompatSchema,
  DrawingStyleSchema,
  drawingSettingsInitialState,
} from '@features/drawing/model/reducers/drawingSettingsReducer.js';
import {
  elevationSettingsInitialState,
  GRADE_WINDOW_WHOLE_LINE,
} from '@features/elevationChart/model/settingsReducer.js';
import { GalleryLicenseSchema } from '@features/gallery/licenses.js';
import { GalleryColorizeBySchema } from '@features/gallery/model/actions.js';
import { gallerySettingsInitialState } from '@features/gallery/model/settingsReducer.js';
import { gpsRecorderSettingsInitialState } from '@features/gpsRecorder/model/settingsReducer.js';
import { RecorderConfigSchema } from '@features/gpsRecorder/protocol.js';
import { homeLocationInitialState } from '@features/homeLocation/model/reducer.js';
import { l10nInitialState } from '@features/l10n/model/reducer.js';
import {
  HeadingSourceSchema,
  locationSettingsInitialState,
} from '@features/location/model/settingsReducer.js';
import { BRIGHTNESS_STEPS } from '@features/map/brightness.js';
import { LayersSettingsCompatSchema } from '@features/map/model/actions.js';
import { LayerSetupsCompatSchema } from '@features/map/model/layerSetup.js';
import {
  MapPresetArrayCompatSchema,
  presetIdOf,
  presetItem,
} from '@features/map/model/mapPreset.js';
import {
  mapInitialState,
  withoutFeaturesLayer,
} from '@features/map/model/reducer.js';
import { mapDetailsInitialState } from '@features/mapDetails/model/reducer.js';
import { MarkerTypeSchema } from '@features/objects/model/actions.js';
import { objectsSettingsInitialState } from '@features/objects/model/settingsReducer.js';
import {
  PanoramaGradientSchema,
  PanoramaGradientStopsSchema,
} from '@features/panorama/gradient.js';
import {
  DEPTH_LIFT_MAX,
  FOV_FULL,
  FOV_MIN,
  LABEL_DENSITY_MAX,
  LABEL_DISTANCE_WEIGHT_MAX,
  LABEL_HAZE_MAX_KM,
  PROMINENCE_WEIGHT_MAX,
  panoramaSettingsInitialState,
  RANGE_MAX_KM,
  RANGE_MIN_KM,
} from '@features/panorama/model/settingsReducer.js';
import { DETAIL_MAX, type PanoramaDetail } from '@features/panorama/quality.js';
import { ShadingSchema } from '@features/parameterizedShading/model/Shading.js';
import { routePlannerInitialState } from '@features/routePlanner/model/reducer.js';
import {
  MAX_ALTERNATIVES,
  routePlannerSettingsInitialState,
} from '@features/routePlanner/model/settingsReducer.js';
import { SearchResultStyleSchema } from '@features/search/model/actions.js';
import { searchSettingsInitialState } from '@features/search/model/settingsReducer.js';
import { trackingSettingsInitialState } from '@features/tracking/model/settingsReducer.js';
import {
  nearestRadiusKm,
  VIEWSHED_DETAIL_ORDER,
  viewshedSettingsInitialState,
} from '@features/viewshed/model/settingsReducer.js';
import { weatherRadarSettingsInitialState } from '@features/weatherRadar/model/settingsReducer.js';
import { ColorizeSettingsShape } from '@shared/colorizers/colorizeSettings.js';
import { ColorizingModeSchema } from '@shared/colorizers/index.js';
import {
  STEEPNESS_DEFAULT_SCALE,
  STEEPNESS_SCALES,
} from '@shared/colorizers/modes/steepness.js';
import { LabelVisibilitySchema } from '@shared/labelVisibility.js';
import { LanguageSchema } from '@shared/langUtils.js';
import {
  CustomLayerDefArrayCompatSchema,
  resolveLayerAliases,
  resolveLayersSettingsAliases,
  SHADING_SOURCE,
} from '@shared/mapDefinitions.js';
import { TransportTypeCompatSchema } from '@shared/transportTypeDefs.js';
import { LatLonSchema } from '@shared/types/common.js';
import storage from 'local-storage-fallback';
import z from 'zod';
import { mainInitialState } from './reducer.js';
import type { RootState } from './store.js';

/** localStorage key holding the serialized persisted Redux state. */
export const STORAGE_KEY = 'store';

export const PersistedAuthSchema = z.object({
  user: z
    .object({
      ...UserSchema.shape,
      settings: UserSettingsCompatSchema.optional().catch(undefined),
    })
    .nullable()
    .optional(),
});

export const PersistedMapSchema = z
  .object({
    lat: z.number(),
    lon: z.number(),
    zoom: z.number(),
    // Each left out on its own when it doesn't parse, rather than the whole
    // slice falling back to its defaults.
    layers: z.array(z.string()).optional().catch(undefined),
    layersSettings: LayersSettingsCompatSchema.optional().catch(undefined),
    layerSetups: LayerSetupsCompatSchema.optional().catch(undefined),
    customLayers: CustomLayerDefArrayCompatSchema.optional().catch(undefined),
    presets: MapPresetArrayCompatSchema.optional().catch(undefined),
    linkPresets: MapPresetArrayCompatSchema.optional().catch(undefined),
    legacyMapWarningSuppressions: z.array(z.string()),
    shadingOnServer: z.boolean(),
    maxZoom: z.number(),
    resolutionScale: z.number().nullable(),
    featureScale: z.number(),
    zoomSnap: z.number(),
    backgroundColor: z.string(),
    backgroundCheckerboard: z.boolean(),
    // Out of range after a change of steps, it alone falls back.
    brightness: z
      .number()
      .int()
      .min(-BRIGHTNESS_STEPS)
      .max(BRIGHTNESS_STEPS)
      .optional()
      .catch(undefined),
  })
  .partial();

const LegacyMapSchema = z.object({
  mapType: z.string(),
  overlays: z.string().array(),
});

const PreSetupsMapSchema = z.object({
  layersSettings: z
    .record(z.string(), z.object({ opacity: z.number().optional() }))
    .optional(),
  shading: ShadingSchema.optional(),
});

// Accepts the legacy `{ mapType, overlays }` shape, mapping it to `{ layers }`,
// and a map from before setups: overlay opacities and the shading moved into
// the setups of their maps.
const PersistedMapCompatSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== 'object') {
    return raw;
  }

  const m = LegacyMapSchema.safeParse(raw);

  const withLayers = m.success
    ? { ...raw, layers: [m.data.mapType, ...m.data.overlays] }
    : raw;

  const pre = PreSetupsMapSchema.safeParse(raw);

  if ('layerSetups' in raw || !pre.success) {
    return withLayers;
  }

  const layerSetups: Record<string, object> = Object.fromEntries(
    Object.entries(pre.data.layersSettings ?? {}).flatMap(([type, s]) =>
      s.opacity === undefined ? [] : [[type, { opacity: s.opacity }]],
    ),
  );

  if (pre.data.shading) {
    layerSetups[SHADING_SOURCE] = {
      ...layerSetups[SHADING_SOURCE],
      shading: pre.data.shading,
    };
  }

  return { ...withLayers, layerSetups };
}, PersistedMapSchema);

export const PersistedL10nSchema = z
  .object({
    chosenLanguage: LanguageSchema.nullable(),
  })
  .partial();

export const PersistedCookieConsentSchema = z
  .object({
    cookieConsentResult: z.boolean().nullable(),
    analyticCookiesAllowed: z.boolean(),
  })
  .partial();

export const PersistedHomeLocationSchema = z
  .object({
    homeLocation: LatLonSchema.nullable(),
  })
  .partial();

export const PersistedMainSchema = z
  .object({
    hiddenInfoBars: z.record(z.string(), z.number()),
    shownInfoBars: z.record(z.string(), z.number()),
  })
  .partial();

export const PersistedObjectsSettingsSchema = z
  .object({
    selectedIcon: MarkerTypeSchema,
    color: z.string(),
    labelVisibility: LabelVisibilitySchema,
  })
  .partial();

// `transportType`/`milestones` stay in the transient route slice (the route
// document state the reducer routes on, also URL-synced and saved per map);
// they are persisted only as last-used defaults that survive a map clear.
export const PersistedRoutePlannerSchema = z
  .object({
    transportType: TransportTypeCompatSchema.catch('hiking'),
    milestones: z.union([z.literal('abs'), z.literal('rel'), z.literal(false)]),
  })
  .partial();

export const PersistedRoutePlannerSettingsSchema = z
  .object({
    ...ColorizeSettingsShape,
    preventHint: z.boolean(),
    lineWidth: z.number(),
    lineOpacity: z.number(),
    markerOpacity: z.number(),
    // Clamped here: rehydration bypasses the reducer. Caught rather than
    // rejected — a rejected field takes the whole slice down (see `rehydrate`).
    maxAlternatives: z
      .number()
      .int()
      .min(1)
      .max(MAX_ALTERNATIVES)
      .catch(routePlannerSettingsInitialState.maxAlternatives),
  })
  .partial();

export const PersistedElevationSettingsSchema = z
  .object({
    despikeWindow: z.number().min(0).max(100),
    ditchFillWindow: z.number().min(0).max(100),
    gradeWindow: z.union([
      z.literal(GRADE_WINDOW_WHOLE_LINE),
      z.number().min(0).max(200),
    ]),
    // Only one of the offered scales: anything else would leave the slider
    // between its stops. Caught rather than rejected — a rejected field takes
    // the whole slice down (see `rehydrate`).
    steepnessScale: z
      .number()
      .refine((scale) =>
        (STEEPNESS_SCALES as readonly number[]).includes(scale),
      )
      .catch(STEEPNESS_DEFAULT_SCALE),
    preventRangeHint: z.boolean(),
  })
  .partial();

const PersistedPanoramaSettingsSchema = z
  .object({
    // The asked-for figures, which may be past what a lapsed account may have:
    // `grantedPanorama` holds the request to the budget, and premium grants
    // them back silently.
    detail: z.union([z.number().positive(), z.literal(DETAIL_MAX)]),
    tilt: z.enum(['standard', 'wide', 'flat', 'custom']),
    altMin: z.number(),
    altMax: z.number(),
    // Bounded because it divides the request's own left edge and the viewer's
    // pan limits; a zero or a negative from a hand-edited store leaves both
    // with nothing to work from.
    fovDeg: z.number().min(FOV_MIN).max(FOV_FULL),
    eye: z.number(),
    // Bounded to what the slider offers: the request adds it to the top of the
    // band, and the service refuses anything past 45 outright.
    depthLift: z.number().min(0).max(DEPTH_LIFT_MAX),
    // The asked-for figure, which may be past what a lapsed account may have —
    // `grantedRangeKm` clamps the request, so premium grants it back silently.
    rangeKm: z.number().min(RANGE_MIN_KM).max(RANGE_MAX_KM),
    showRevealedLabels: z.boolean(),
    ridgeStrength: z.number(),
    ridgeWidth: z.number(),
    ridgeColor: z.string(),
    groundColor: z.string(),
    // Caught rather than refused, as the viewshed radius is: these are the only
    // fields here a user can put out of bounds by hand — 33 stops is a few
    // clicks on the gradient bar — and a refusal takes the whole slice's
    // defaults with it, resetting the quality, the band and every label slider
    // over a ramp.
    groundGradient: PanoramaGradientSchema.nullable().catch(null),
    recentGradients: z.array(PanoramaGradientStopsSchema).catch([]),
    // Bounded, because the level indexes the menu's icon and word arrays.
    labelDensity: z.number().int().min(0).max(LABEL_DENSITY_MAX),
    showLabelEle: z.boolean(),
    showLabelDistance: z.boolean().catch(false),
    showLabelProminence: z.boolean().catch(false),
    showLabelIsolation: z.boolean().catch(false),
    minDominance: z.number(),
    // Bounded to what the sliders offer: all three reach `rankLabels`, where a
    // stored value from outside the range would order the names by something
    // no control can undo.
    labelHazeKm: z.number().min(0).max(LABEL_HAZE_MAX_KM),
    labelDistanceWeight: z.number().min(0).max(LABEL_DISTANCE_WEIGHT_MAX),
    prominenceWeight: z.number().min(0).max(PROMINENCE_WEIGHT_MAX),
    autoPan: z.boolean(),
    showSun: z.boolean(),
    showMoon: z.boolean(),
  })
  .partial();

/** The detail each named tier asked for; its sampling is now derived. */
const PANORAMA_TIER_DETAIL: Record<string, PanoramaDetail> = {
  superfast: 5,
  fast: 10,
  standard: 20,
  detailed: 25,
  finest: 30,
  maximum: DETAIL_MAX,
};

const PersistedPanoramaSettingsCompatSchema = z.preprocess((s) => {
  if (!s || typeof s !== 'object' || !('quality' in s)) {
    return s;
  }

  const { quality, ...rest } = s as { quality: unknown };

  const detail =
    typeof quality === 'string' ? PANORAMA_TIER_DETAIL[quality] : undefined;

  return detail === undefined ? rest : { ...rest, detail };
}, PersistedPanoramaSettingsSchema);

const PersistedViewshedSettingsSchema = z
  .object({
    // The stops the control offers, not a range: one between two would leave
    // the dropdown showing nothing while still costing rays. Snapped to the
    // nearest rather than refused — a failed field here would take the whole
    // slice's defaults with it, resetting the colour and the rest over a radius
    // nobody can see, and a store written when the near stops were offered
    // still says what its owner asked for.
    radiusKm: z
      .number()
      .positive()
      .transform(nearestRadiusKm)
      .catch(viewshedSettingsInitialState.radiusKm),
    detail: z.enum(VIEWSHED_DETAIL_ORDER),
    eye: z.number().min(0),
    targetHeight: z.number().min(0),
    color: z.string(),
    // The service's own bounds, which it answers 400 to rather than clamping.
    gamma: z.number().min(0.1).max(10),
    alphaFloor: z.number().min(0).max(1),
  })
  .partial();

export const PersistedSearchSettingsSchema = z
  .object({
    resultStyle: SearchResultStyleSchema.partial(),
    labelVisibility: LabelVisibilitySchema,
  })
  .partial();

export const PersistedDataViewerSettingsSchema = z
  .object({
    style: DrawingStyleSchema.partial(),
    labelVisibility: LabelVisibilitySchema,
    colorizeTrackBy: ColorizingModeSchema.nullable(),
    colorizeLegend: z.boolean(),
  })
  .partial();

export const PersistedTrackingSettingsSchema = z
  .object({
    ...ColorizeSettingsShape,
    showLine: z.boolean(),
    showPoints: z.boolean(),
  })
  .partial();

export const PersistedGpsRecorderSettingsSchema = z
  .object({
    ...RecorderConfigSchema.shape,
    splitGapS: z.number().nonnegative(),
    feedLocation: z.boolean(),
    keepScreenAwake: z.boolean(),
    style: DrawingStyleSchema.partial(),
  })
  .partial();

// Historical provider names, kept deliberately — see `MapDetailsSource`. One
// unrecognised entry fails the whole slice here, silently resetting the user's
// source filter.
const MapDetailsSourceSchema = z.union([
  z.literal('nominatim-reverse'),
  z.literal('overpass-nearby'),
  z.literal('overpass-surrounding'),
  z.custom<`wms:${string}`>(
    (v) => typeof v === 'string' && v.startsWith('wms:'),
  ),
]);

export const PersistedMapDetailsSchema = z
  .object({
    excludeSources: z.array(MapDetailsSourceSchema),
  })
  .partial();

export const PersistedLocationSettingsSchema = z
  .object({
    headingSource: HeadingSourceSchema,
    showBearingLine: z.boolean(),
  })
  .partial();

export const PersistedGallerySettingsSchema = z
  .object({
    colorizeBy: GalleryColorizeBySchema.nullable(),
    recentTags: z.array(z.string()),
    showDirection: z.boolean(),
    showLegend: z.boolean(),
    premium: z.boolean(),
    license: GalleryLicenseSchema,
  })
  .partial();

export const PersistedWeatherRadarSettingsSchema = z
  .object({ showNowcast: z.boolean() })
  .partial();

/**
 * Single source of truth for persisted state. One entry per slice drives both
 * the save side (`selectPersistedState`, used by `statePersistingMiddleware`)
 * and the rehydrate side (`getInitialState`), so persisting a new field means
 * editing one entry.
 */
type PersistEntry<K extends keyof RootState = keyof RootState> = {
  key: K;
  /** Validates the persisted blob for this slice. */
  schema: z.ZodType;
  /** Slice initial state, used as the merge base on rehydrate. */
  initial: RootState[K];
  /** Another slice this data may also live under (e.g. `main`); read if the primary is absent/empty. */
  fallbackKey?: keyof RootState;
  /** Merge the `fallbackKey` data under the primary (fill gaps) instead of letting a non-empty primary win — needed only when the primary key predates a migrated field (currently just `trackViewerSettings`, whose key already held `style`). */
  mergeFallback?: boolean;
  /** Merge parsed data over `initial`. Default: `{ ...initial, ...data }`. */
  rehydrate?: (initial: RootState[K], data: any) => RootState[K];
  /** What to write to localStorage on save; omit to make the slice rehydrate-only. */
  persist?: (slice: RootState[K]) => unknown;
};

/** Captures per-key typing at the definition site; widens to the union in the array. */
function defineEntry<K extends keyof RootState>(
  entry: PersistEntry<K>,
): PersistEntry {
  return entry as unknown as PersistEntry;
}

const PersistedCachedMapsSettingsSchema = z
  .object({
    mode: z.enum([
      'network-only',
      'network-first',
      'cache-first',
      'cache-only',
    ]),
    store: z.boolean(),
    maxAgeDays: z.number(),
    maxSizeMb: z.number(),
  })
  .partial();

const PERSIST: PersistEntry[] = [
  defineEntry({
    key: 'map',
    schema: PersistedMapCompatSchema,
    initial: mapInitialState,
    // Rehydration writes the slice directly, so the zoom bypasses the reducer's
    // own snapping. A stored zoom can disagree with the stored `zoomSnap` when
    // the grid was coarsened by something other than the preference — a rebuilt
    // default, or one browser's storage read by another build — and the map
    // would then sit a fraction off what the store and the URL claim.
    rehydrate: (initial, data) => {
      // A field that didn't parse is undefined, and keeps the default.
      const merged = {
        ...initial,
        ...Object.fromEntries(
          Object.entries(data).filter(([, value]) => value !== undefined),
        ),
      } as typeof initial;

      const presetIds = new Set(
        [...merged.presets, ...merged.linkPresets].map((p) => p.id),
      );

      return {
        ...merged,
        layers: withoutFeaturesLayer(
          resolveLayerAliases(merged.layers).filter((item) => {
            const id = presetIdOf(item);

            // `_<id>`: a legacy map-combination marker.
            return id === undefined ? !item.startsWith('_') : presetIds.has(id);
          }),
        ),
        layersSettings: resolveLayersSettingsAliases(merged.layersSettings),
        layerSetups: resolveLayersSettingsAliases(merged.layerSetups),
        zoom: merged.zoomSnap
          ? Math.round(merged.zoom / merged.zoomSnap) * merged.zoomSnap
          : merged.zoom,
      };
    },
    persist: (m) => ({
      layersSettings: m.layersSettings,
      lat: m.lat,
      lon: m.lon,
      zoom: m.zoom,
      layers: m.layers,
      layerSetups: m.layerSetups,
      customLayers: m.customLayers,
      presets: m.presets,
      // Only those still on the map.
      linkPresets: m.linkPresets.filter((p) =>
        m.layers.includes(presetItem(p.id)),
      ),
      legacyMapWarningSuppressions: m.legacyMapWarningSuppressions,
      shadingOnServer: m.shadingOnServer,
      maxZoom: m.maxZoom,
      resolutionScale: m.resolutionScale,
      featureScale: m.featureScale,
      zoomSnap: m.zoomSnap,
      backgroundColor: m.backgroundColor,
      backgroundCheckerboard: m.backgroundCheckerboard,
      brightness: m.brightness,
    }),
  }),
  defineEntry({
    key: 'l10n',
    schema: PersistedL10nSchema,
    initial: l10nInitialState,
    persist: (s) => ({ chosenLanguage: s.chosenLanguage }),
  }),
  defineEntry({
    key: 'auth',
    schema: PersistedAuthSchema,
    initial: authInitialState,
    rehydrate: (initial, data) => ({
      ...initial,
      user: data.user === undefined ? initial.user : data.user,
    }),
    persist: (a) => ({
      user: a.user && {
        ...a.user,
        premiumExpiration: a.user.premiumExpiration
          ? (a.user.premiumExpiration?.toISOString() ?? null)
          : null,
      },
    }),
  }),
  defineEntry({
    key: 'cookieConsent',
    schema: PersistedCookieConsentSchema,
    initial: cookieConsentInitialState,
    fallbackKey: 'main',
    persist: (c) => ({
      cookieConsentResult: c.cookieConsentResult,
      analyticCookiesAllowed: c.analyticCookiesAllowed,
    }),
  }),
  defineEntry({
    key: 'drawingSettings',
    schema: DrawingSettingsCompatSchema,
    initial: drawingSettingsInitialState,
    fallbackKey: 'main',
    rehydrate: (initial, data) => ({
      ...initial,
      ...data,
      style: { ...initial.style, ...data.style },
    }),
    persist: (s) => s,
  }),
  defineEntry({
    key: 'homeLocation',
    schema: PersistedHomeLocationSchema,
    initial: homeLocationInitialState,
    fallbackKey: 'main',
    persist: (h) => ({ homeLocation: h.homeLocation }),
  }),
  defineEntry({
    key: 'main',
    schema: PersistedMainSchema,
    initial: mainInitialState,
    persist: (s) => ({
      hiddenInfoBars: s.hiddenInfoBars,
      shownInfoBars: s.shownInfoBars,
    }),
  }),
  defineEntry({
    key: 'objectsSettings',
    schema: PersistedObjectsSettingsSchema,
    initial: objectsSettingsInitialState,
    persist: (o) => ({
      selectedIcon: o.selectedIcon,
      color: o.color,
      labelVisibility: o.labelVisibility,
    }),
  }),
  defineEntry({
    key: 'routePlanner',
    schema: PersistedRoutePlannerSchema,
    initial: routePlannerInitialState,
    persist: (r) => ({
      transportType: r.transportType,
      milestones: r.milestones,
    }),
  }),
  defineEntry({
    key: 'routePlannerSettings',
    schema: PersistedRoutePlannerSettingsSchema,
    initial: routePlannerSettingsInitialState,
    // Also read these prefs from the `routePlanner` blob, which can carry them.
    fallbackKey: 'routePlanner',
    persist: (s) => ({
      colorizeBy: s.colorizeBy,
      colorizeLegend: s.colorizeLegend,
      preventHint: s.preventHint,
      lineWidth: s.lineWidth,
      lineOpacity: s.lineOpacity,
      markerOpacity: s.markerOpacity,
      maxAlternatives: s.maxAlternatives,
    }),
  }),
  defineEntry({
    key: 'searchSettings',
    schema: PersistedSearchSettingsSchema,
    initial: searchSettingsInitialState,
    rehydrate: (initial, data) => ({
      ...initial,
      ...data,
      resultStyle: { ...initial.resultStyle, ...data.resultStyle },
    }),
    persist: (s) => ({
      resultStyle: s.resultStyle,
      labelVisibility: s.labelVisibility,
    }),
  }),
  defineEntry({
    key: 'panoramaSettings',
    schema: PersistedPanoramaSettingsCompatSchema,
    initial: panoramaSettingsInitialState,
    persist: (p) => p,
  }),
  defineEntry({
    key: 'elevationSettings',
    schema: PersistedElevationSettingsSchema,
    initial: elevationSettingsInitialState,
    persist: (e) => ({
      despikeWindow: e.despikeWindow,
      ditchFillWindow: e.ditchFillWindow,
      gradeWindow: e.gradeWindow,
      steepnessScale: e.steepnessScale,
      preventRangeHint: e.preventRangeHint,
    }),
  }),
  defineEntry({
    key: 'trackViewerSettings',
    schema: PersistedDataViewerSettingsSchema,
    initial: dataViewerSettingsInitialState,
    // Also read the colorize prefs from the legacy `trackViewer` blob. This
    // slice's key predates the colorize move (it persisted `style`), so the
    // fallback must fill colorize per key rather than be shadowed by the
    // already-present primary.
    fallbackKey: 'trackViewer',
    mergeFallback: true,
    rehydrate: (initial, data) => ({
      ...initial,
      ...data,
      style: { ...initial.style, ...data.style },
    }),
    persist: (t) => ({
      style: t.style,
      labelVisibility: t.labelVisibility,
      colorizeTrackBy: t.colorizeTrackBy,
      colorizeLegend: t.colorizeLegend,
    }),
  }),
  defineEntry({
    key: 'gpsRecorderSettings',
    schema: PersistedGpsRecorderSettingsSchema,
    initial: gpsRecorderSettingsInitialState,
    rehydrate: (initial, data) => ({
      ...initial,
      ...data,
      style: { ...initial.style, ...data.style },
    }),
    persist: (g) => ({
      intervalMs: g.intervalMs,
      minDistanceM: g.minDistanceM,
      maxAccuracyM: g.maxAccuracyM,
      priority: g.priority,
      source: g.source,
      splitGapS: g.splitGapS,
      feedLocation: g.feedLocation,
      keepScreenAwake: g.keepScreenAwake,
      style: g.style,
    }),
  }),
  defineEntry({
    key: 'trackingSettings',
    schema: PersistedTrackingSettingsSchema,
    initial: trackingSettingsInitialState,
    // Also read these prefs from the `tracking` blob, which can carry them.
    fallbackKey: 'tracking',
    persist: (t) => ({
      colorizeBy: t.colorizeBy,
      colorizeLegend: t.colorizeLegend,
      showLine: t.showLine,
      showPoints: t.showPoints,
    }),
  }),
  defineEntry({
    key: 'mapDetails',
    schema: PersistedMapDetailsSchema,
    initial: mapDetailsInitialState,
    persist: (m) => ({ excludeSources: m.excludeSources }),
  }),
  defineEntry({
    key: 'locationSettings',
    schema: PersistedLocationSettingsSchema,
    initial: locationSettingsInitialState,
    persist: (l) => ({
      headingSource: l.headingSource,
      showBearingLine: l.showBearingLine,
    }),
  }),
  defineEntry({
    key: 'gallerySettings',
    schema: PersistedGallerySettingsSchema,
    initial: gallerySettingsInitialState,
    // Also read these prefs from the `gallery` blob, which can carry them.
    fallbackKey: 'gallery',
    persist: (g) => ({
      colorizeBy: g.colorizeBy,
      recentTags: g.recentTags,
      showDirection: g.showDirection,
      showLegend: g.showLegend,
      premium: g.premium,
      license: g.license,
    }),
  }),
  defineEntry({
    key: 'cachedMapsSettings',
    schema: PersistedCachedMapsSettingsSchema,
    initial: cachedMapsSettingsInitialState,
    persist: (s) => s,
  }),
  defineEntry({
    key: 'weatherRadarSettings',
    schema: PersistedWeatherRadarSettingsSchema,
    initial: weatherRadarSettingsInitialState,
    persist: (r) => ({ showNowcast: r.showNowcast }),
  }),
  defineEntry({
    key: 'viewshedSettings',
    schema: PersistedViewshedSettingsSchema,
    initial: viewshedSettingsInitialState,
    persist: (v) => v,
  }),
];

export function getInitialState(): Partial<RootState> {
  let persisted: Partial<Record<keyof RootState, unknown>>;

  try {
    persisted = JSON.parse(storage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    persisted = {};
  }

  const initial: Partial<RootState> = {};

  for (const entry of PERSIST) {
    const raw = persisted[entry.key];

    let data: unknown;

    if (entry.fallbackKey) {
      data = parseWithFallback(
        entry.schema,
        raw,
        persisted[entry.fallbackKey],
        entry.mergeFallback,
      );
    } else {
      const result = entry.schema.safeParse(raw);

      data = result.success ? result.data : undefined;
    }

    if (data === undefined) {
      continue;
    }

    (initial as Record<keyof RootState, unknown>)[entry.key] = entry.rehydrate
      ? entry.rehydrate(entry.initial as never, data)
      : { ...(entry.initial as object), ...(data as object) };
  }

  return initial;
}

/**
 * The slices {@link selectPersistedState} reads. Nothing else in the store can
 * change what gets written, so identical references across all of them mean the
 * write would be byte-for-byte what is already there.
 */
export const PERSISTED_KEYS: (keyof RootState)[] = PERSIST.filter(
  (entry) => entry.persist,
).map((entry) => entry.key);

export function selectPersistedState(state: RootState): Partial<RootState> {
  return Object.fromEntries(
    PERSIST.flatMap((entry) =>
      entry.persist
        ? [[entry.key, entry.persist(state[entry.key] as never)]]
        : [],
    ),
  ) as Partial<RootState>;
}

function parseWithFallback<T>(
  schema: z.ZodType<T>,
  primary: unknown,
  fallback: unknown,
  merge = false,
): T | undefined {
  const a = schema.safeParse(primary);

  const primaryData =
    a.success && Object.keys(a.data as object).length > 0 ? a.data : undefined;

  if (primaryData && !merge) {
    return primaryData;
  }

  const b = schema.safeParse(fallback);

  const fallbackData =
    b.success && Object.keys(b.data as object).length > 0 ? b.data : undefined;

  // With `merge`, the fallback supplies the base and the primary overrides per
  // key, so a value left behind in the legacy slice fills a key the primary
  // lacks (needed when the primary key predates the migrated field). Otherwise
  // a non-empty primary wins outright.
  if (merge) {
    return primaryData || fallbackData
      ? ({ ...(fallbackData as object), ...(primaryData as object) } as T)
      : undefined;
  }

  return fallbackData;
}
